import { getAbsoluteImageUrl } from '../../utils/imageUrl';

export interface PhotoGalleryItem {
  url: string;
  caption: string;
  isPrimary?: boolean;
  uploadDate?: string;
  photoId?: string;
  photoType?: string; // 'document_front' | 'document_back' — undefined means identity photo
  /**
   * The untouched upload behind `url`, when the row has one (migration 230 onward).
   *
   * `url` stays the square crop because that is what every consumer reads and what the
   * viewer is addressed by; this is what a human should be shown and what a re-crop must
   * start from. Undefined means no original exists — rows predating the migration lost
   * theirs in the browser — and callers fall back to `url`.
   */
  originalUrl?: string;
}

interface IdentityPhotoRecord {
  id: string;
  photo_url: string;
  original_photo_url?: string;
  is_primary: boolean;
  created_at: string;
  photo_type?: string;
}

interface GalleryPerson {
  identity_photo_url?: string;
  profile_picture_url?: string;
  document_front_url?: string;
  document_back_url?: string;
  identity_photos?: IdentityPhotoRecord[];
  vehicles?: Array<{ id: string; plate?: string; is_active?: boolean; photo_url?: string }>;
}

/**
 * The vehicle photo on this person's file — the slot beside Documento (Frente)/(Verso).
 *
 * It is a photo of the person's file, not a view onto a vehicle record: the car a visitor
 * arrived in, or a resident's main car, captured once by VisitorRegistrationModal as
 * photo_type=vehicle and treated as a document everywhere else (DocumentsTab's "VEÍCULO FORA
 * DO PADRÃO" flag, Editar Todos' third column).
 *
 * It deliberately does NOT fall back to `vehicles[].photo_url`. Doing so made a person whose
 * file had no vehicle photo borrow the picture of whichever registered vehicle came first in
 * the array, under the unqualified label "Veículo". The registered vehicles' own photos
 * belong to the VEÍCULOS tab, where each is shown against its plate.
 */
export function personVehiclePhoto(
  personData: GalleryPerson | null | undefined,
): IdentityPhotoRecord | null {
  return personData?.identity_photos?.find(p => p.photo_type === 'vehicle') ?? null;
}

/** Absolutized original of a record, or undefined when it has none. */
const originalOf = (record?: IdentityPhotoRecord): string | undefined =>
  record?.original_photo_url ? getAbsoluteImageUrl(record.original_photo_url) : undefined;

/**
 * Every photo attached to a person, in the order the lightbox pages through them.
 *
 * Every URL stored here is absolutized (getAbsoluteImageUrl) so the exact-match lookup below
 * works regardless of whether the underlying field was relative or already-absolute — every
 * click handler that calls openPhotoViewer() passes an already-absolutized URL, so gallery
 * entries must match that format or findIndex() silently fails and falls back to index 0 (the
 * main photo), which is exactly the "clicking the vehicle/document thumbnail opens the
 * person's main photo instead" bug this guards against.
 */
export function buildPhotoGallery(
  personData: GalleryPerson | null | undefined,
  startFromUrl?: string,
): { gallery: PhotoGalleryItem[]; startIndex: number } {
  if (!personData) return { gallery: [], startIndex: 0 };

  const gallery: PhotoGalleryItem[] = [];

  // Add primary identity photo first
  const identityPhotos = personData.identity_photos?.filter(p => !p.photo_type?.startsWith('document')) || [];
  const primaryPhotoRecord = identityPhotos.find(p => p.is_primary) || identityPhotos.find(p => p.photo_url === personData.identity_photo_url);
  if (personData.identity_photo_url) {
    gallery.push({
      url: getAbsoluteImageUrl(personData.identity_photo_url),
      caption: 'Foto de Identificação (Principal)',
      isPrimary: true,
      photoId: primaryPhotoRecord?.id,
      originalUrl: originalOf(primaryPhotoRecord),
    });
  }

  // Add other identity photos
  personData.identity_photos?.forEach((photo) => {
    if (photo.photo_type?.startsWith('document')) return; // skip doc photos here
    // Skip if it's already the primary photo we added
    if (photo.photo_url !== personData.identity_photo_url) {
      gallery.push({
        url: getAbsoluteImageUrl(photo.photo_url),
        caption: photo.is_primary ? 'Foto de Identificação (Principal)' : 'Foto de Identificação',
        isPrimary: photo.is_primary,
        uploadDate: photo.created_at,
        photoId: photo.id,
        originalUrl: originalOf(photo),
      });
    }
  });

  // Add profile picture if different from identity photo
  if (personData.profile_picture_url && personData.profile_picture_url !== personData.identity_photo_url) {
    gallery.push({
      url: getAbsoluteImageUrl(personData.profile_picture_url),
      caption: 'Foto de Perfil',
    });
  }

  // Add document photos (front / back). Documents are photographed, never cropped on the
  // client, so there is no original to keep for them.
  const docPhotos = personData.identity_photos?.filter(p => p.photo_type?.startsWith('document')) || [];
  const docFrontRecord = docPhotos.find(p => p.photo_type === 'document_front');
  const docBackRecord = docPhotos.find(p => p.photo_type === 'document_back');
  const docFrontUrl = docFrontRecord?.photo_url || personData.document_front_url;
  const docBackUrl = docBackRecord?.photo_url || personData.document_back_url;
  if (docFrontUrl) gallery.push({ url: getAbsoluteImageUrl(docFrontUrl), caption: 'Documento — Frente', photoId: docFrontRecord?.id, photoType: 'document_front' });
  if (docBackUrl) gallery.push({ url: getAbsoluteImageUrl(docBackUrl), caption: 'Documento — Verso', photoId: docBackRecord?.id, photoType: 'document_back' });

  // The vehicle slot on the person's file. Like the documents it is photographed rather than
  // cropped, so there is no original to keep — and like them it carries its row id, which is
  // what lets the viewer offer ATUALIZAR over it.
  const vehicleRecord = personVehiclePhoto(personData);
  if (vehicleRecord?.photo_url) {
    gallery.push({
      url: getAbsoluteImageUrl(vehicleRecord.photo_url),
      caption: 'Veículo',
      photoId: vehicleRecord.id,
      photoType: 'vehicle',
    });
  }

  // Find starting index. startFromUrl is already absolutized by every caller of
  // openPhotoViewer(), so it's compared against the equally-absolutized entries above.
  const startIndex = startFromUrl
    ? Math.max(0, gallery.findIndex((p) => p.url === startFromUrl))
    : 0;

  return { gallery, startIndex };
}

/** What replacing this photo would send, or null when the photo has no destination. */
export interface PhotoEditRequest {
  photoId?: string;
  url: string;
  photoType?: string;
}

/**
 * Where a replacement for this photo belongs — the one rule behind both the ATUALIZAR button
 * and what it sends.
 *
 * A photo is replaceable exactly when it has a stored row behind it. The vehicle slot used to
 * be built from a vehicle record, which has no row, so ATUALIZAR never appeared over it — it
 * was the one photo in the viewer an operator could only look at. Deciding the gate and the
 * payload in one place keeps the button from being shown without somewhere to put the photo.
 */
export function photoEditRequest(item: PhotoGalleryItem): PhotoEditRequest | null {
  if (!item.photoId) return null;
  return {
    photoId: item.photoId,
    // Re-crop from the untouched upload, not from the crop already in place — cropping a crop
    // is how a face gets cut off a second time, and is why the original is stored. Falls back
    // to the crop for rows that predate migration 230.
    url: item.originalUrl || item.url,
    photoType: item.photoType,
  };
}
